import request from '../../utils/request';

export function getRoutesByDate(params) {
  return request('/routes/get-by-date', {
    method: 'POST',
    data: params,
  });
}

export function getLogGmproByDate(params) {
  return request('/routes/get-log-gmpro-by-date', {
    method: 'POST',
    data: params,
  });
}

export function dispatchRoutes(params) {
  return request('/routes/dispatch', {
    method: 'POST',
    data: params,
  });
}

export function optimizeRoutesByDate(params) {
  return request('/routes/optimize', {
    method: 'POST',
    data: params,
  });
}

export function optimizeGmproRoutesByDate(params) {
  return request('/routes/optimize-gmpro', {
    method: 'POST',
    data: params,
  });
}

// #125 — outcome of an optimize / reorder run ({ status: PENDING | COMPLETED |
// FAILED, message }), polled by the day plan when the socket event is missed.
export function getOptimizeRun(runId) {
  return request(`/routes/optimize-runs/${runId}`, {
    method: 'GET',
  });
}

export function getRoutes(params) {
  return request('/routes', {
    method: 'GET',
    params: params,
  });
}

export function getRoute(id) {
  return request(`/routes/${id}`, {
    method: 'GET',
  });
}

export function deleteRoute(id, deleteOrders = false, force = false) {
  // OVO-126 — when deleteOrders is true, the backend also deletes the route's
  // orders instead of detaching them back to UNASSIGNED.
  // `force` (Felipe, Aug 2026) additionally bypasses the in-progress/completed
  // guard: PERMANENT removal of the route, its history and its orders. The UI
  // gates it behind typing the route id.
  const params = [];
  if (deleteOrders) params.push('deleteOrders=true');
  if (force) params.push('force=true');
  const qs = params.length ? `?${params.join('&')}` : '';
  return request(`/routes/${id}${qs}`, {
    method: 'DELETE',
  });
}

export function reorderOrdersForRoutes(params) {
  const date = params.date;
  delete params.date;

  return request(`/routes/${date}/reorder-orders`, {
    method: 'POST',
    data: params,
  });
}

// OVO-020 — operator-simulated driver step action on a route. Same payload
// shape as the mobile API; backend stamps the resulting OrderLog rows with
// simulatedByOperator=true and userId=<operator>.
export function operatorStepAction(routeId, payload) {
  return request(`/routes/${routeId}/operator-step-action`, {
    method: 'POST',
    data: payload,
  });
}

// OVO-224 — recover a wrongly-skipped/failed order back to a deliverable state.
export function operatorRecoverStep(routeId, payload) {
  return request(`/routes/${routeId}/operator-recover-step`, {
    method: 'POST',
    data: payload,
  });
}

// OVO-020 — operator-simulated route-level action (START / COMPLETED / …).
export function operatorRouteAction(routeId, payload) {
  return request(`/routes/${routeId}/operator-route-action`, {
    method: 'POST',
    data: payload,
  });
}

// OVO-128 — operator correction of the route's actual shift start/end.
// Drivers frequently forget to START/END the route in the app, leaving
// actualShiftStart/End wrong. PATCH; audit-logged server-side. Each field:
// ISO string to set, null to clear, omit to leave untouched.
export function operatorEditRouteShift(routeId, payload) {
  return request(`/routes/${routeId}/operator-shift`, {
    method: 'PATCH',
    data: payload,
  });
}

// P1 Pause/Resume — operator adds a pause the driver forgot to badge
// (backdated PAUSE+RESUME log pair, audited). Returns the refreshed route.
export function addRoutePause(routeId, payload) {
  return request(`/routes/${routeId}/operator-pause`, {
    method: 'POST',
    data: payload,
  });
}

// Undo of the above — only operator-added pauses are deletable.
export function deleteRoutePause(routeId, pauseLogId) {
  return request(`/routes/${routeId}/operator-pause/${pauseLogId}`, {
    method: 'DELETE',
  });
}

// #135 — undo the system's auto-close of a route the driver forgot to end.
export function revertRouteAutoClose(routeId) {
  return request(`/routes/${routeId}/revert-auto-close`, { method: 'POST' });
}

// #133 — correct the start / end of any pause (driver or operator). The
// driver's original times are kept server-side; revert restores them.
export function editRoutePause(routeId, pauseLogId, payload) {
  return request(`/routes/${routeId}/operator-pause/${pauseLogId}`, {
    method: 'PATCH',
    data: payload,
  });
}

export function revertRoutePauseEdit(routeId, pauseLogId) {
  return request(`/routes/${routeId}/operator-pause/${pauseLogId}/revert`, {
    method: 'POST',
  });
}

// OVO-203 — reassign a route to another driver without re-optimizing.
export function changeRouteDriver(routeId, driverId) {
  return request(`/routes/${routeId}/change-driver`, {
    method: 'POST',
    data: { driverId },
  });
}

// OVO-128 / OVO-129 (core) — ask the backend to estimate the actual shift
// start/end from the route's optimizer data (first-stop arrival − hub leg,
// last-stop finish + return leg). Read-only; used to pre-fill the editor.
export function getRouteShiftEstimate(routeId) {
  return request(`/routes/${routeId}/shift-estimate`, {
    method: 'GET',
  });
}

// Route inconsistency detection (read-only) — Monitoring → Inconsistencies tab.
// Returns routes whose recorded data is internally inconsistent (v1: the
// recorded end falls before the driver could have arrived back at the hub).
export function getRouteInconsistencies(params) {
  return request('/routes/inconsistencies', {
    method: 'GET',
    params,
  });
}

// Operator-edit audit trail for a route (shift-time corrections, etc.) —
// backs the route detail "Edit history" section.
export function getRouteAuditLog(routeId) {
  return request(`/routes/${routeId}/audit-log`, { method: 'GET' });
}

// Mark a detected inconsistency as a false positive (e.g. traffic jam) / undo.
export function dismissRouteInconsistency(routeId, reason) {
  return request(`/routes/${routeId}/dismiss-inconsistency`, { method: 'POST', data: { reason } });
}

export function restoreRouteInconsistency(routeId) {
  return request(`/routes/${routeId}/restore-inconsistency`, { method: 'POST' });
}

// OVO-260 — auto-correct a badged-too-late shift end to the last VALIDATED
// stop (+ the return leg). The original badge is kept so it can be undone.
export function correctRouteShiftEnd(routeId) {
  return request(`/routes/${routeId}/correct-shift-end`, { method: 'POST', data: {} });
}

// Bulk over a date range. Always call with `dryRun: true` first — the preview
// is what the operator confirms.
export function correctInconsistentShifts({ dateFrom, dateTo, dryRun, routeIds }) {
  return request('/routes/correct-inconsistent-shifts', {
    method: 'POST',
    data: { dateFrom, dateTo, dryRun, routeIds },
  });
}

export function revertRouteShiftCorrection(routeId) {
  return request(`/routes/${routeId}/revert-shift-correction`, { method: 'POST' });
}

// OVO-266 — ETA backtest: old plan vs new service-time formula vs actual,
// with optional param overrides for live tuning.
export function getEtaBacktest(routeId, params) {
  return request(`/routes/${routeId}/eta-backtest`, { method: 'GET', params });
}
